import { type ParsedUrlQuery } from "querystring"
import { type WizardSteps } from "@krakentech/blueprint-onboarding/next-wizard/pages-router"
import { isDualEnergy, isElectricityOnly, isGasOnly } from "@/features/subscription"
import { OnboardingRouteNames } from "@/routes/onboarding"
import { telesalesRoutesPaths } from "@/routes/telesales"
import { type OnboardingFormValues } from "../types/onboardingFormValues"

function getPaymentDetailsStepQueryParams(formValues: OnboardingFormValues) {
    const queryParams: ParsedUrlQuery = {
        quoteCode: formValues.quoteCode,
        accountNumber: formValues.accountNumber || undefined,
    }

    if (formValues.electricity.ledgerNumber) {
        queryParams.electricityLedger = formValues.electricity.ledgerNumber
    }

    if (formValues.gas.ledgerNumber) {
        queryParams.gasLedger = formValues.gas.ledgerNumber
    }

    return queryParams
}

function getQuoteTypeQueryParams(formValues: OnboardingFormValues) {
    const queryParams: ParsedUrlQuery = {}

    if (isElectricityOnly(formValues.selectedEnergyTypes)) {
        queryParams.produit = "electricite"
    } else if (isGasOnly(formValues.selectedEnergyTypes)) {
        queryParams.produit = "gaz"
    } else if (isDualEnergy(formValues.selectedEnergyTypes)) {
        queryParams.produit = "dual"
    }

    return queryParams
}

export function getTelesalesSteps(formValues: OnboardingFormValues) {
    if (!formValues.joinSupplierProcessNumber) {
        throw new Error("joinSupplierProcessNumber is required to get telesales steps")
    }

    return [
        {
            name: OnboardingRouteNames.PaymentDetails,
            pathname: telesalesRoutesPaths.index.resolve({
                pathParams: {
                    joinSupplierProcessNumber: formValues.joinSupplierProcessNumber,
                },
            }),
            query: getPaymentDetailsStepQueryParams(formValues),
        },
        {
            name: OnboardingRouteNames.PaymentDetailsFinancialSituation,
            pathname: telesalesRoutesPaths.paymentDetailsFinancialSituation.resolve(),
            query: getQuoteTypeQueryParams(formValues),
        },
        {
            name: OnboardingRouteNames.PaymentDetailsRetry,
            pathname: telesalesRoutesPaths.paymentDetailsRetry.resolve(),
            query: getQuoteTypeQueryParams(formValues),
        },
        {
            name: OnboardingRouteNames.PaymentDetailsErrorSignature,
            pathname: telesalesRoutesPaths.paymentDetailsErrorSignature.resolve(),
            query: getQuoteTypeQueryParams(formValues),
        },
        {
            name: OnboardingRouteNames.PaymentDetailsErrorIban,
            pathname: telesalesRoutesPaths.paymentDetailsErrorIban.resolve(),
            query: getQuoteTypeQueryParams(formValues),
        },
        {
            name: OnboardingRouteNames.PaymentDetailsUnpaidBills,
            pathname: telesalesRoutesPaths.paymentDetailsUnpaidBills.resolve(),
            query: getQuoteTypeQueryParams(formValues),
        },
        {
            name: OnboardingRouteNames.OrderReview,
            pathname: telesalesRoutesPaths.orderReview.resolve(),
            query: getQuoteTypeQueryParams(formValues),
        },
        {
            name: OnboardingRouteNames.PrePayment,
            pathname: telesalesRoutesPaths.prePayment.resolve(),
            query: getQuoteTypeQueryParams(formValues),
        },
        {
            name: OnboardingRouteNames.PaymentDetailsErrorPrepayment,
            pathname: telesalesRoutesPaths.paymentDetailsErrorPrepayment.resolve(),
            query: getQuoteTypeQueryParams(formValues),
        },
        {
            name: OnboardingRouteNames.PaymentDetailsPendingPrepayment,
            pathname: telesalesRoutesPaths.paymentDetailsPendingPrepayment.resolve(),
        },
        {
            name: OnboardingRouteNames.SecurityDeposit,
            pathname: telesalesRoutesPaths.securityDeposit.resolve(),
            query: getQuoteTypeQueryParams(formValues),
        },
        {
            name: OnboardingRouteNames.Onboarded,
            pathname: telesalesRoutesPaths.onboarded.resolve(),
            query: getQuoteTypeQueryParams(formValues),
        },
    ] as const satisfies WizardSteps<OnboardingRouteNames>
}
